import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Icons } from '@/shared/constants/icons';
import { ROUTES } from '@/shared/constants/routes';
import { PlatformIcon, type PlatformId } from '@/shared/components/PlatformIcon';
import { useRealtimeSubscription } from '@/shared/hooks/useRealtimeSubscription';
import { playMessageChime } from '@/shared/lib/message-chime';
import { alertFromRow, useMessageAlerts, type MessageAlert } from '@/shared/stores/message-alerts.store';

/** How long a card stays when nobody touches it. */
const CARD_MS = 8000;

function initial(name: string | null): string {
  const c = name?.trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

function AlertCard({ alert, onOpen, onDismiss }: { alert: MessageAlert; onOpen: () => void; onDismiss: () => void }) {
  const { t } = useTranslation();
  const reduced = useReducedMotion() ?? false;
  const timer = useRef<number | null>(null);
  const start = (): void => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(onDismiss, CARD_MS);
  };
  const stop = (): void => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => {
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id]);

  const channel = t(`inbox.channels.${alert.platform}`, { defaultValue: alert.platform });
  const name = alert.fromName ?? t('inbox.alert.someone');
  return (
    <motion.div
      layout={!reduced}
      initial={reduced ? { opacity: 0 } : { opacity: 0, x: 24, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={reduced ? { opacity: 0 } : { opacity: 0, x: 24 }}
      transition={{ duration: 0.18 }}
      onMouseEnter={stop}
      onMouseLeave={start}
      onFocus={stop}
      onBlur={start}
      className="relative pointer-events-auto w-full rounded-[16px] border border-[var(--line)] bg-[var(--color-surface)] shadow-[var(--shadow-md,0_8px_24px_rgba(0,0,0,0.12))] overflow-hidden"
      role="status"
      data-testid="message-alert"
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-start gap-3 p-3 pe-10 text-start bg-transparent border-0 cursor-pointer hover:bg-[var(--color-surface-2)]"
        aria-label={t('inbox.alert.openFrom', { name, channel })}
      >
        <span className="relative shrink-0">
          <span
            className="w-10 h-10 rounded-full flex items-center justify-center text-ui font-bold"
            style={{ background: 'var(--color-surface-2)', color: 'var(--ink-2)' }}
            aria-hidden="true"
          >
            {initial(alert.fromName)}
          </span>
          <span className="absolute -bottom-0.5 -end-0.5 rounded-full p-[2px]" style={{ background: 'var(--color-surface)' }}>
            <PlatformIcon platform={alert.platform.toLowerCase() as PlatformId} size={14} decorative />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-1.5">
            <span className="text-ui font-semibold truncate" style={{ color: 'var(--ink)' }} dir="auto">{name}</span>
            <span className="text-meta shrink-0" style={{ color: 'var(--ink-3)' }}>{channel}</span>
          </span>
          <span className="block text-caption mt-0.5 line-clamp-2 break-words" style={{ color: 'var(--ink-2)' }} dir="auto">
            {alert.text ?? t('inbox.alert.attachment')}
          </span>
          <span className="inline-flex items-center gap-1 mt-1.5 text-caption font-semibold" style={{ color: 'var(--accent-text, var(--ink))' }}>
            {t('inbox.alert.open')}
            <Icons.chevronRight size={14} aria-hidden="true" />
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-2 end-2 w-8 h-8 rounded-full flex items-center justify-center bg-transparent border-0 cursor-pointer hover:bg-[var(--color-surface-2)]"
        style={{ color: 'var(--ink-3)' }}
        aria-label={t('common.close')}
      >
        <Icons.close size={16} aria-hidden="true" />
      </button>
    </motion.div>
  );
}

/**
 * New customer messages, anywhere in the app: a soft chime (unless turned off
 * in the inbox), a card top-right with who wrote, on which channel and the
 * first lines (tap to open the conversation), and "(2) …" in the tab title
 * while the tab is in the background. On the inbox itself the list shows the
 * message live, so only the chime plays there. A desktop notification is
 * shown too when the browser already allows them for this site (never asked
 * for here).
 */
export function MessageAlerts({ workspaceId }: { workspaceId: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const alerts = useMessageAlerts((s) => s.alerts);
  const unseen = useMessageAlerts((s) => s.unseen);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  useRealtimeSubscription({
    table: 'inbox_events',
    filter: workspaceId ? `workspace_id=eq.${workspaceId}` : undefined,
    events: ['INSERT'],
    // The nav badge follows new messages live instead of polling.
    invalidateKeys: [['inbox', 'conversation-counts']],
    enabled: !!workspaceId,
    onEvent: (_event, row) => {
      const alert = alertFromRow(row);
      if (!alert) return;
      const store = useMessageAlerts.getState();
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      if (store.soundOn) playMessageChime();
      if (hidden) {
        store.addUnseen();
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            const n = new Notification(alert.fromName ?? t('inbox.alert.someone'), {
              body: alert.text ?? t('inbox.alert.attachment'),
              tag: alert.conversationKey,
            });
            n.onclick = () => {
              window.focus();
              void navigate(`${ROUTES.INBOX}?conversation=${encodeURIComponent(alert.conversationKey)}`);
              n.close();
            };
          } catch {
            // Some browsers only allow notifications from a service worker.
          }
        }
      }
      if (!pathRef.current.startsWith(ROUTES.INBOX)) store.push(alert);
    },
  });

  // The tab title carries the count of messages that came in while away.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') useMessageAlerts.getState().resetUnseen();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s/, '');
    document.title = unseen > 0 ? `(${unseen}) ${base}` : base;
  }, [unseen, pathname]);

  // Opening the inbox shows every new message there: the cards go.
  useEffect(() => {
    if (pathname.startsWith(ROUTES.INBOX)) useMessageAlerts.getState().clear();
  }, [pathname]);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed z-[60] top-[72px] end-4 start-4 sm:start-auto sm:w-[360px] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
      aria-label={t('inbox.alert.region')}
    >
      <AnimatePresence initial={false}>
        {alerts.map((a) => (
            <AlertCard
              key={a.id}
              alert={a}
              onDismiss={() => useMessageAlerts.getState().dismiss(a.id)}
              onOpen={() => {
                useMessageAlerts.getState().dismiss(a.id);
                void navigate(`${ROUTES.INBOX}?conversation=${encodeURIComponent(a.conversationKey)}`);
              }}
            />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
